// The Markdown "rich text" written by components/RichText.jsx — guideline
// introductions and sections, and an incident's details and notes.
//
// RichTextView renders it with react-markdown, but the Excel and PDF exports have
// no Markdown renderer to hand, so the parsing lives here and both use it. Only
// what the editor's toolbar can produce is recognised: **bold**, *italic*,
// <u>underline</u>, bulleted and numbered lists, headings, links and photos.

import axios from "axios";
import { API_BASE_URL } from "./config";

// A Markdown image: "![alt](url)". Photos are inserted on a line of their own.
const IMAGE_RE = /!\[([^\]]*)\]\(([^)]+)\)/g;
const IMAGE_LINE_RE = /^!\[([^\]]*)\]\(([^)]+)\)$/;

// Photos and links carry no emphasis, so flatten them up front: a photo becomes a
// marker (the exports can't always show the image where it sat) and a link becomes
// "text (url)".
const stripRefs = (s) => (s || "")
  .replace(IMAGE_RE, (_m, alt) => (alt ? `[photo: ${alt}]` : "[photo]"))
  .replace(/\[([^\]]+)\]\(([^)]+)\)/g, "$1 ($2)");

/**
 * Split an inline string into styled runs, each carrying its combined
 * bold/italic/underline state (back-ticks are dropped).
 */
export function parseInline(input) {
  const s = stripRefs(input);
  const runs = [];
  const state = { bold: false, italic: false, underline: false };
  let buf = "";
  const flush = () => { if (buf) { runs.push({ text: buf, ...state }); buf = ""; } };
  let i = 0;
  while (i < s.length) {
    if (s.startsWith("**", i)) { flush(); state.bold = !state.bold; i += 2; continue; }
    if (s.toLowerCase().startsWith("<u>", i)) { flush(); state.underline = true; i += 3; continue; }
    if (s.toLowerCase().startsWith("</u>", i)) { flush(); state.underline = false; i += 4; continue; }
    if (s[i] === "*") { flush(); state.italic = !state.italic; i += 1; continue; }
    if (s[i] === "`") { flush(); i += 1; continue; }
    buf += s[i];
    i += 1;
  }
  flush();
  return runs;
}

/** The jsPDF font style for one run. */
export const styleName = (r) =>
  (r.bold && r.italic) ? "bolditalic" : r.bold ? "bold" : r.italic ? "italic" : "normal";

/** Turn a Markdown rich-text string into an ordered list of render blocks. */
export function parseRichText(md) {
  const lines = (md || "").replace(/\r/g, "").split("\n");
  const blocks = [];
  let para = [];
  const flush = () => {
    if (para.length) { blocks.push({ type: "para", text: para.join(" ") }); para = []; }
  };
  for (const raw of lines) {
    const line = raw.trim();
    const img = line.match(IMAGE_LINE_RE);
    if (img) { flush(); blocks.push({ type: "image", url: img[2] }); continue; }
    if (!line) { flush(); continue; }
    const h = line.match(/^(#{1,6})\s+(.*)$/);
    if (h) { flush(); blocks.push({ type: "subheading", text: h[2] }); continue; }
    const ul = line.match(/^[-*+]\s+(.*)$/);
    if (ul) { flush(); blocks.push({ type: "li", prefix: "•  ", text: ul[1] }); continue; }
    const ol = line.match(/^(\d+)\.\s+(.*)$/);
    if (ol) { flush(); blocks.push({ type: "li", prefix: `${ol[1]}. `, text: ol[2] }); continue; }
    para.push(line);
  }
  flush();
  return blocks;
}

/**
 * Rich text as readable plain text: formatting markers removed, list markers kept,
 * line breaks left alone (a spreadsheet cell and a PDF paragraph both want them).
 *
 * `photos` says what to do with an embedded photo — "mark" leaves a "[photo]" in
 * its place, for a reader who can't be shown the image; "drop" removes the line,
 * for a renderer that draws the photo itself.
 */
export function plainText(md, { photos = "mark" } = {}) {
  const lines = (md || "").replace(/\r/g, "").split("\n");
  const out = [];
  for (const line of lines) {
    if (photos === "drop" && IMAGE_LINE_RE.test(line.trim())) continue;
    out.push(parseInline(line).map(r => r.text).join(""));
  }
  // Blank lines around a dropped photo would otherwise pile up.
  return out.join("\n").replace(/\n{3,}/g, "\n\n").trim();
}

/** Every photo URL referenced by a rich-text string, in the order they appear. */
export function imageUrls(md) {
  return [...(md || "").matchAll(IMAGE_RE)].map(m => m[2]);
}

/**
 * Fetch a photo and return it as a data URL for jsPDF. The endpoints need the
 * bearer token, so this goes through axios (which attaches it) rather than letting
 * jsPDF load the URL itself.
 */
export async function fetchImageDataUrl(url) {
  const full = url.startsWith("http") ? url : `${API_BASE_URL}${url}`;
  const res = await axios.get(full, { responseType: "blob" });
  return await new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = reject;
    fr.readAsDataURL(res.data);
  });
}

/**
 * Pre-fetch every given photo URL as a data URL, keyed by URL. One that can't be
 * fetched is simply absent, so the caller can render a placeholder rather than
 * failing the whole export.
 */
export async function fetchImageDataUrls(urls) {
  const out = {};
  await Promise.all([...new Set(urls)].map(async (url) => {
    try { out[url] = await fetchImageDataUrl(url); }
    catch { /* leave missing */ }
  }));
  return out;
}
